import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Send, MailCheck, X, UserRound } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import AssetHoverLabel from '../components/AssetHoverLabel';
import SecurityFilterBar from '../components/SecurityFilterBar';
import TenantLink from '../components/TenantLink';
import ColumnPicker, { ColumnDef, DraggableTh, SortValue, useColumnPrefs, useColumnSort } from '../components/ColumnPicker';
import StatusSelect from '../components/StatusSelect';
import { countryTag } from '../utils/country';
import OutcomeBadge, { EventOutcome } from '../components/OutcomeBadge';
import CustomerIpBadge from '../components/CustomerIpBadge';
import TenantSelect from '../components/TenantSelect';

interface Alert {
  id: number;
  outcome: EventOutcome | null;
  host: string | null;
  host_label: string | null;
  message: string;
  source: string;
  created_at: string;
  dedup_count: number;
  last_seen_at: string | null;
  last_reported_at: string | null;
  rule_name: string | null;
  case_id: number;
  case_title: string;
  case_severity: 'low' | 'medium' | 'high' | 'critical';
  case_status: 'new' | 'investigating' | 'contained' | 'closed';
  case_mitre_tactic: string | null;
  tenant_id: number;
  tenant_name?: string;
  tenant_contact_email: string | null;
  // Asse identità / origine (2026-09-18).
  entity_user: string | null;
  src_ip: string | null;
  src_country: string | null;
  asset_id: number | null;
  asset_ip_address: string | null;
  asset_mac_address: string | null;
  asset_business_critical: boolean | null;
  // Blocco di IP pubblici del cliente che contiene src_ip (null se nessuno).
  customer_ip: { cidr: string; label: string | null; siteName: string | null } | null;
}

const SEVERITY_LABELS: Record<string, string> = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };

const SEVERITY_BADGE: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700',
  critical: 'bg-red-600 text-white',
};

// Colonne disponibili (ordine di visualizzazione).
const ALERT_COLUMNS: ColumnDef[] = [
  { key: 'severity', label: 'Severity', defaultVisible: false },
  { key: 'outcome', label: 'Esito', defaultVisible: true },
  { key: 'tenant', label: 'Tenant', defaultVisible: true },
  { key: 'last_detected', label: 'Last Detected', defaultVisible: true },
  { key: 'first_detected', label: 'First Detected', defaultVisible: false },
  { key: 'rule', label: 'Rule', defaultVisible: true },
  { key: 'host', label: 'Host', defaultVisible: true },
  { key: 'user', label: 'User', defaultVisible: true },
  { key: 'origin', label: 'Origin', defaultVisible: true },
  { key: 'message', label: 'Message', defaultVisible: true },
  { key: 'aggregated', label: 'Aggregated Events', defaultVisible: false },
  { key: 'data_source', label: 'Data Source', defaultVisible: false },
  { key: 'alert_id', label: 'Alert ID', defaultVisible: false },
  { key: 'mitre', label: 'MITRE Tactic', defaultVisible: false },
  { key: 'incident', label: 'Incident', defaultVisible: true },
  { key: 'incident_status', label: 'Incident Status', defaultVisible: true },
];

const SEVERITY_RANK: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };
const STATUS_RANK: Record<string, number> = { new: 1, investigating: 2, contained: 3, closed: 4 };

// Valore con cui ordinare una colonna (vedi useColumnSort in ColumnPicker.tsx).
function sortValue(a: Alert, key: string): SortValue {
  switch (key) {
    case 'severity': return SEVERITY_RANK[a.case_severity];
    case 'outcome': return a.outcome;
    case 'tenant': return a.tenant_name;
    case 'last_detected': return a.last_seen_at || a.created_at;
    case 'first_detected': return a.created_at;
    case 'rule': return a.rule_name;
    case 'host': return a.host_label ? `${a.host_label} · ${a.host}` : a.host;
    case 'user': return a.entity_user;
    case 'origin': return a.src_ip;
    case 'message': return a.message;
    case 'aggregated': return a.dedup_count;
    case 'data_source': return a.source;
    case 'alert_id': return a.id;
    case 'mitre': return a.case_mitre_tactic;
    case 'incident': return a.case_title;
    case 'incident_status': return STATUS_RANK[a.case_status];
    default: return null;
  }
}

// Bozza precompilata della segnalazione al cliente per un singolo alert: l'analista può
// modificarla prima dell'invio (stesso pattern dei report a livello di incident).
function buildDefaultReport(a: Alert): string {
  const asset = [a.host, a.asset_ip_address].filter(Boolean).join(' / ') || 'not identified';
  const occurrences = a.dedup_count > 1
    ? `${a.dedup_count} (from ${new Date(a.created_at).toLocaleString('en-GB')} to ${new Date(a.last_seen_at || a.created_at).toLocaleString('en-GB')})`
    : '1';
  return [
    `Dear Customer,`,
    ``,
    `our SOC team detected a security alert on your infrastructure.`,
    ``,
    `Alert: ${a.rule_name || 'Unclassified alert'}`,
    `Severity: ${SEVERITY_LABELS[a.case_severity]}`,
    `Affected asset: ${asset}`,
    `Detected at: ${new Date(a.created_at).toLocaleString('en-GB')}`,
    `Occurrences: ${occurrences}`,
    `Detail: ${a.message}`,
    `Related incident: ${a.case_title}`,
    ``,
    `Our analysts are handling the alert and will keep you updated on the remediation activities.`,
    ``,
    `Best regards,`,
    `SOC Team`,
  ].join('\n');
}

// Vedi CasesPage.tsx: stessa finestra di default per non caricare l'intera storia.
function defaultSince(hours: number): string {
  const d = new Date(Date.now() - hours * 3_600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Flusso grezzo di tutti gli alert (prima/indipendentemente dalla correlazione in incident),
// con la stessa barra filtri condivisa di Security Incidents — per il tenant selezionato
// oppure di tutti i tenant accessibili.
export default function AlertsPage() {
  const { selectedTenantId } = useTenantScope();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Default High/Critical (26/10/2026, richiesta dell'utente): si apre su ciò che va guardato
  // subito; le altre severità si aggiungono da Filters.
  const [severities, setSeverities] = useState<Set<string>>(() => new Set(['high', 'critical']));
  const [statuses, setStatuses] = useState<Set<string>>(new Set());
  // Default (26/10/2026): ultime 24 ore e refresh automatico ogni 5 minuti.
  const [fromDate, setFromDate] = useState(() => defaultSince(24));
  const [toDate, setToDate] = useState('');
  const [hideNewAssets, setHideNewAssets] = useState(true);
  const [hideEdrGap, setHideEdrGap] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(300);
  const [reportAlert, setReportAlert] = useState<Alert | null>(null);
  const [reportSubject, setReportSubject] = useState('');
  const [reportBody, setReportBody] = useState('');
  const [reportError, setReportError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const { orderedDefs, visible, setVisible: setVisibleColumns, moveColumn, reset: resetColumns } = useColumnPrefs('soc.columns.alerts', ALERT_COLUMNS);
  const getSortValue = useCallback(sortValue, []);
  const { sort, toggleSort, sorted: sortedAlerts } = useColumnSort(alerts, getSortValue);

  // Colonne effettivamente mostrate (nell'ordine scelto): Tenant solo nella vista cross-tenant.
  const shownColumns = orderedDefs.filter(d => visible.has(d.key) && (d.key !== 'tenant' || !selectedTenantId));
  const colCount = shownColumns.length + 3;

  async function load() {
    setLoading(true);
    const res = await axios.get('/api/alerts', {
      params: {
        tenantId: selectedTenantId ?? undefined,
        limit: 200,
        search: search || undefined,
        severity: severities.size > 0 ? [...severities].join(',') : undefined,
        status: statuses.size > 0 ? [...statuses].join(',') : undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        hideNewAssets: hideNewAssets || undefined,
        hideEdrGap: hideEdrGap || undefined,
      },
    });
    setAlerts(res.data.data);
    setSelected(new Set());
    setLoading(false);
  }

  function toggleSelected(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected(prev => (prev.size === alerts.length ? new Set() : new Set(alerts.map(a => a.id))));
  }

  // Fixing massivo come in Security Incidents (2026-10-06): lo stato vive sull'incident padre,
  // quindi si cambiano gli incident degli alert selezionati — e con loro tutti gli altri alert
  // correlati agli stessi incident, anche se non selezionati.
  const selectedCaseIds = [...new Set(alerts.filter(a => selected.has(a.id)).map(a => a.case_id))];

  async function bulkSetStatus(status: string) {
    if (selectedCaseIds.length === 0) return;
    await axios.put('/api/cases/bulk/status', { caseIds: selectedCaseIds, status });
    load();
  }

  useEffect(() => { load(); }, [selectedTenantId, severities, statuses, fromDate, toDate, hideNewAssets, hideEdrGap]);

  // Ricerca testuale con debounce leggero per non interrogare ad ogni battitura.
  useEffect(() => {
    const timeout = setTimeout(load, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(load, autoRefresh * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedTenantId, severities, statuses, fromDate, toDate, hideNewAssets, hideEdrGap]);

  // Lo stato è quello dell'incident padre: cambiarlo qui aggiorna tutti gli alert correlati.
  async function setCaseStatus(caseId: number, status: string) {
    setAlerts(prev => prev.map(a => (a.case_id === caseId ? { ...a, case_status: status as Alert['case_status'] } : a)));
    await axios.put(`/api/cases/${caseId}`, { status });
    load();
  }

  function openReport(a: Alert) {
    setReportAlert(a);
    setReportSubject(`[SOC] ${SEVERITY_LABELS[a.case_severity]} alert: ${a.rule_name || a.host}`);
    setReportBody(buildDefaultReport(a));
    setReportError(null);
  }

  async function sendReport() {
    if (!reportAlert) return;
    setSending(true);
    setReportError(null);
    try {
      await axios.post(`/api/alerts/${reportAlert.id}/report`, { subject: reportSubject, message: reportBody });
      setReportAlert(null);
      load();
    } catch (err: any) {
      setReportError(err.response?.data?.error || 'Error while sending the report');
    } finally {
      setSending(false);
    }
  }

  function renderCell(a: Alert, key: string) {
    switch (key) {
      case 'severity':
        return <span className={`badge ${SEVERITY_BADGE[a.case_severity]}`}>{a.case_severity}</span>;
      case 'outcome':
        return <OutcomeBadge outcome={a.outcome} />;
      case 'tenant':
        return <TenantLink tenantId={a.tenant_id} name={a.tenant_name} />;
      case 'last_detected':
        return <span className="text-gray-500 whitespace-nowrap">{new Date(a.last_seen_at || a.created_at).toLocaleString('en-GB')}</span>;
      case 'first_detected':
        return <span className="text-gray-500 whitespace-nowrap">{new Date(a.created_at).toLocaleString('en-GB')}</span>;
      case 'rule':
        return (
          <span className="font-medium text-primary-700">
            <Link to={`/alerts/${a.id}`} className="hover:underline">{a.rule_name || 'Unclassified alert'}</Link>
            {a.last_reported_at && (
              <span className="ml-2 inline-flex items-center gap-1 badge bg-emerald-100 text-emerald-700"
                title={`Reported on ${new Date(a.last_reported_at).toLocaleString('en-GB')}`}>
                <MailCheck size={11} /> Reported
              </span>
            )}
          </span>
        );
      case 'host':
        if (!a.host) return <span className="text-gray-400">—</span>;
        return (
          <span className="inline-flex items-center gap-1.5">
            <AssetHoverLabel hostname={a.host} label={a.host_label} ipAddress={a.asset_ip_address} macAddress={a.asset_mac_address} assetId={a.asset_id} />
            {a.asset_business_critical && (
              <span className="badge bg-red-100 text-red-700" title="Business Critical Asset">BCA</span>
            )}
          </span>
        );
      case 'user':
        return a.entity_user
          ? <span className="inline-flex items-center gap-1 text-gray-800"><UserRound size={13} className="text-violet-500" />{a.entity_user}</span>
          : <span className="text-gray-400">—</span>;
      case 'origin': {
        if (!a.src_ip) return <span className="text-gray-400">—</span>;
        const t = countryTag(a.src_country);
        return (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="font-mono text-xs text-gray-700">{a.src_ip}</span>
            {a.customer_ip
              ? <CustomerIpBadge label={a.customer_ip.label} siteName={a.customer_ip.siteName} />
              : t && <span className="badge bg-gray-100 text-gray-700" title={t.title}>{t.text}</span>}
          </span>
        );
      }
      case 'message':
        return (
          <span className="font-mono text-xs">
            {a.message}
            {a.dedup_count > 1 && (
              <span className="badge bg-primary-50 text-primary-700 ml-2 font-sans"
                title={`${a.dedup_count} identical events grouped — last at ${new Date(a.last_seen_at || a.created_at).toLocaleString('en-GB')}`}>
                ×{a.dedup_count}
              </span>
            )}
          </span>
        );
      case 'aggregated':
        return <span className="badge bg-gray-100 text-gray-600">{a.dedup_count}</span>;
      case 'data_source':
        return <span className="text-gray-500 uppercase">{a.source || '—'}</span>;
      case 'alert_id':
        return <span className="font-mono text-xs text-gray-500">alert-{a.id}</span>;
      case 'mitre':
        return <span className="text-gray-500">{a.case_mitre_tactic || '—'}</span>;
      case 'incident':
        return (
          <span className="font-medium text-primary-700">
            <Link to={`/incidents/${a.case_id}`}>{a.case_title}</Link>
            <span className={`badge ml-2 ${SEVERITY_BADGE[a.case_severity]}`}>{a.case_severity}</span>
          </span>
        );
      case 'incident_status':
        return <StatusSelect value={a.case_status} onChange={s => setCaseStatus(a.case_id, s)} />;
      default: return null;
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Security Alerts</h1>
        <TenantSelect />
      </div>

      <SecurityFilterBar
        search={search}
        onSearchChange={setSearch}
        severities={severities}
        onSeveritiesChange={setSeverities}
        statuses={statuses}
        onStatusesChange={setStatuses}
        fromDate={fromDate}
        onFromDateChange={setFromDate}
        toDate={toDate}
        onToDateChange={setToDate}
        hideNewAssets={hideNewAssets}
        onHideNewAssetsChange={setHideNewAssets}
        hideEdrGap={hideEdrGap}
        onHideEdrGapChange={setHideEdrGap}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
        onRefresh={load}
        loading={loading}
        items={alerts.map(a => ({ severity: a.case_severity, mitre_tactic: a.case_mitre_tactic }))}
      />

      {selected.size > 0 && (
        <div className="card p-3 mb-3 flex items-center gap-2 bg-primary-50 border-primary-200">
          <span className="text-sm text-primary-800 font-medium">
            {selected.size} selected ({selectedCaseIds.length} incident{selectedCaseIds.length === 1 ? '' : 's'})
          </span>
          <button onClick={() => bulkSetStatus('investigating')} className="btn-secondary">Mark as Fixing</button>
          <button onClick={() => bulkSetStatus('closed')} className="btn-secondary">Mark as Fixed</button>
          <span className="text-xs text-primary-700/70">The status belongs to the incident: every alert of the same incident changes with it.</span>
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="table-header-sm w-8">
                <input type="checkbox" checked={selected.size > 0 && selected.size === alerts.length} onChange={toggleSelectAll} />
              </th>
              {shownColumns.map(d => (
                <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} sort={sort} onSort={toggleSort} className="table-header-sm whitespace-nowrap">
                  {d.label}
                </DraggableTh>
              ))}
              <th className="table-header-sm text-right">Report</th>
              <th className="table-header-sm w-8 text-right">
                <ColumnPicker defs={orderedDefs} visible={visible} onChange={setVisibleColumns} onReset={resetColumns} onMove={moveColumn} />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="table-cell-sm text-gray-400" colSpan={colCount}>Loading…</td></tr>}
            {!loading && alerts.length === 0 && (
              <tr><td className="table-cell-sm text-gray-400" colSpan={colCount}>No alert matches the current filters</td></tr>
            )}
            {sortedAlerts.map(a => (
              <tr key={a.id}
                className={`border-t border-gray-100 hover:bg-gray-50 ${
                  a.asset_business_critical ? 'bg-red-50/70 border-l-4 border-l-red-400'
                  : a.last_reported_at ? 'bg-emerald-50/70 border-l-4 border-l-emerald-400' : ''}`}>
                <td className="table-cell-sm">
                  <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelected(a.id)} />
                </td>
                {shownColumns.map(d => <td key={d.key} className="table-cell-sm">{renderCell(a, d.key)}</td>)}
                <td className="table-cell-sm text-right" colSpan={2}>
                  <button onClick={() => openReport(a)} disabled={!a.tenant_contact_email}
                    title={a.tenant_contact_email
                      ? `Send a report to ${a.tenant_contact_email}`
                      : 'No contact email configured for this tenant'}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                      a.tenant_contact_email
                        ? 'text-primary-700 bg-primary-50 hover:bg-primary-100'
                        : 'text-gray-300 bg-gray-50 cursor-not-allowed'}`}>
                    <Send size={13} /> Report
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reportAlert && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Send size={16} className="text-primary-600" /> Send report to customer
              </h2>
              <button onClick={() => setReportAlert(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              {reportError && <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{reportError}</div>}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Recipient</label>
                <div className="text-sm text-gray-800 bg-gray-50 rounded-md px-3 py-2">{reportAlert.tenant_contact_email}</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Subject</label>
                <input value={reportSubject} onChange={e => setReportSubject(e.target.value)} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Message</label>
                <textarea value={reportBody} onChange={e => setReportBody(e.target.value)} rows={14}
                  className="input-field w-full font-mono text-xs" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => setReportAlert(null)} className="btn-secondary">Cancel</button>
              <button onClick={sendReport} disabled={sending || !reportBody.trim()} className="btn-primary flex items-center gap-1">
                <Send size={14} /> {sending ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
